import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
// "Complete?" modal — opens when a user clicks the past-block prompt on a
// calendar block. Three-step flow:
//
//   Step 1  "Was this completed?"           Done | Not yet
//   Step 2  "What percent are you at?"      0-100 input
//             (auto-fills 100 if Done, current% if Not yet; user can adjust)
//   Step 3  Only when final < 100% AND there's a linked task:
//             "Finish later or hand off?"   Reschedule | Request handoff
//             Reschedule  → parent opens a follow-up BlockEditor
//             Handoff     → pick coworker + optional note, POST to backend.
//                           Status goes pending → admin/manager reviews.
//
// Free-form blocks (no taskId) skip the % + handoff steps — there's no
// project task to track or hand off; we just flip block.is_completed when
// the user picks "Done".
import { useEffect, useState } from "react";
import { api, ApiError } from "../api/client";
import { useOrgCatalog } from "../hooks/useOrgCatalog";
// ISO Monday-of-week for the current calendar week. Kill List items are
// scoped to a week so the user's "this week's commit list" stays clean.
function mondayOfThisWeekISO() {
    const now = new Date();
    // getDay() returns 0 (Sun) .. 6 (Sat). Convert so Monday = 0.
    const dow = (now.getDay() + 6) % 7;
    const monday = new Date(now);
    monday.setDate(now.getDate() - dow);
    return ymdLocal(monday);
}
// Local-tz YYYY-MM-DD so the /time-blocks range query lines up with the
// week the user *thinks* they're in (not the UTC date).
function ymdLocal(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}
// Round a minutes value to the nearest 15-minute increment, floored at 15
// (a block can't be shorter than one slot). 16 → 15, 23 → 30, 38 → 45.
function roundTo15(min) {
    return Math.max(15, Math.round(min / 15) * 15);
}
export default function ProgressPromptModal({ open, blockTitle, blockId, blockType, taskId, checklistItemId, initialPercent, blockStartAt, blockEndAt, goalId, goalUnit, goalSessionTarget, onClose, onSaved, onScheduleFollowUp, }) {
    const isFlex = blockType === "flex";
    // Goal session block — completing it logs units toward the goal rather than
    // asking how long it took. Takes priority over the time_taken step.
    const isGoal = !!goalId;
    const unitLabel = (goalUnit ?? "").trim();
    // Scheduled length of the block in minutes (null if we can't derive it).
    const scheduledMinutes = (() => {
        if (!blockStartAt || !blockEndAt)
            return null;
        const s = new Date(blockStartAt).getTime();
        const e = new Date(blockEndAt).getTime();
        if (Number.isNaN(s) || Number.isNaN(e) || e <= s)
            return null;
        return Math.round((e - s) / 60000);
    })();
    const [step, setStep] = useState(isFlex ? "flex_category" : "complete_q");
    const [percent, setPercent] = useState(initialPercent);
    // Actual minutes spent — captured on the "How long did it take?" step
    // before completing. Defaults to the full scheduled duration.
    const [actualMinutes, setActualMinutes] = useState(scheduledMinutes ?? 30);
    // Units finished this goal session — string so the field can be empty.
    // Pre-filled with the planned per-session amount when one was scheduled.
    const [goalUnits, setGoalUnits] = useState(goalSessionTarget != null ? String(goalSessionTarget) : "");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    // Handoff form state — loaded lazily when the user picks "Request handoff".
    const [coworkers, setCoworkers] = useState([]);
    const [coworkersLoading, setCoworkersLoading] = useState(false);
    const [handoffUserId, setHandoffUserId] = useState("");
    const [handoffNote, setHandoffNote] = useState("");
    // Whether we successfully re-added the task to the user's Kill List after
    // a partial-progress save. Surface a small confirmation in the next step
    // so the user knows the task is back on their plate without having to
    // open the scheduler. `null` = haven't tried, true/false = result.
    const [killListAdded, setKillListAdded] = useState(null);
    // Server detail message when the kill-list re-add fails — surfaced in
    // the failure banner so the user sees the real reason (e.g. "finish
    // task X first") instead of the generic 'couldn't add' line.
    const [killListReason, setKillListReason] = useState(null);
    // Flex sub-state. flexCategory chooses between the Kill / OPS / Other
    // branches; flexLinkedOptions holds the loaded picker options (kill
    // list rows + the user's incomplete Deep Focus blocks); flexSelectedKey
    // is the option the user picked (synthetic "kill:<id>" /
    // "block:<id>" key); flexDesc is the free-text "what did you do"
    // entry for OPS/Admin.
    const [flexCategory, setFlexCategory] = useState(null);
    const [flexLinkedOptions, setFlexLinkedOptions] = useState([]);
    const [flexKillLoading, setFlexKillLoading] = useState(false);
    const [flexSelectedKey, setFlexSelectedKey] = useState("");
    const [flexDesc, setFlexDesc] = useState("");
    // OPS / Admin classification: the user picks a business unit + activity
    // (e.g. OPS + Admin) so the flex time buckets correctly in the reports
    // instead of landing in "Unassigned". The catalog comes from the shared,
    // cached useOrgCatalog hook (aliased to the names the JSX already uses).
    const [opsBuId, setOpsBuId] = useState("");
    const [opsActivityId, setOpsActivityId] = useState("");
    const { businessUnits: orgBusinessUnits, activities: orgActivities, loading: orgsLoading, } = useOrgCatalog();
    // Reset state every time the modal opens — same component instance is
    // reused across multiple blocks, so a leftover step from the last block
    // would put the user in the wrong place.
    useEffect(() => {
        if (!open)
            return;
        setStep(isFlex ? "flex_category" : "complete_q");
        setPercent(initialPercent);
        setActualMinutes(scheduledMinutes ?? 30);
        setGoalUnits(goalSessionTarget != null ? String(goalSessionTarget) : "");
        setError(null);
        setHandoffUserId("");
        setHandoffNote("");
        setKillListAdded(null);
        setFlexCategory(null);
        setFlexSelectedKey("");
        setFlexDesc("");
        setOpsBuId("");
        setOpsActivityId("");
    }, [open, initialPercent, isFlex, goalSessionTarget]);
    if (!open)
        return null;
    const isLinkedTask = !!taskId;
    // ── Step 1: Done / Not yet ────────────────────────────────────────────
    function answerDone(done) {
        // ALWAYS walk to the percent step on either answer — including for
        // free-form blocks that have no linked task. Previously "Not yet"
        // on a free-form block silently closed the modal, which surfaced
        // as the reported bug "hitting Not yet does nothing". Even without
        // a task to save the % to, capturing the number lets the
        // reschedule flow seed the follow-up block with the right
        // remaining estimate. Done is pre-filled at 100; Not yet starts
        // at whatever the backend already had (or 50 % as a sensible
        // default for free-form rows that don't carry a task progress).
        if (done) {
            // Goal session → ask how many units they finished (logged to the goal),
            // not how long it took. Takes priority over the time_taken step.
            if (isGoal) {
                setStep("goal_units");
                return;
            }
            // Completing → first confirm how long it actually took so we can trim
            // the block to the real time spent (rounded to 15 min). Only when we
            // know the block's scheduled length; otherwise fall through to the
            // normal percent=100 completion with no resize.
            setPercent(100);
            if (scheduledMinutes != null) {
                setActualMinutes(scheduledMinutes);
                setStep("time_taken");
                return;
            }
            setStep("percent");
            return;
        }
        // "Not yet" on a goal session — they didn't do it. Don't log any units;
        // just mark the block reviewed so the prompt stops nagging, then close.
        if (isGoal) {
            void markGoalReviewed();
            return;
        }
        const fallback = isLinkedTask ? Math.max(initialPercent, 0) : 50;
        setPercent(fallback);
        setStep("percent");
    }
    // ── Step 1b: how long did it actually take? → complete + resize ───────
    // Marks the block complete AND trims it to the rounded actual time. Mirrors
    // the done-path of savePercentAndBranch (task→100%, block→is_completed) but
    // adds end_at so Deep Focus / day-summary reflect time truly spent.
    async function saveCompleteWithTime() {
        setSaving(true);
        setError(null);
        try {
            const rounded = roundTo15(actualMinutes);
            let endAtIso = null;
            if (blockStartAt) {
                const start = new Date(blockStartAt).getTime();
                if (!Number.isNaN(start)) {
                    endAtIso = new Date(start + rounded * 60_000).toISOString();
                }
            }
            const blockPatch = { is_completed: true };
            if (endAtIso)
                blockPatch.end_at = endAtIso;
            if (checklistItemId && blockId) {
                // Cascade ticks the checklist item + recomputes task progress.
                await api.patch(`/time-blocks/${blockId}`, blockPatch);
            }
            else if (taskId) {
                await api.patch(`/tasks/${taskId}`, { progress_percent: 100 });
                if (blockId)
                    await api.patch(`/time-blocks/${blockId}`, blockPatch);
            }
            else if (blockId) {
                await api.patch(`/time-blocks/${blockId}`, blockPatch);
            }
            onSaved();
            onClose();
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Save failed");
        }
        finally {
            setSaving(false);
        }
    }
    // ── Goal session: log units → add to the goal + complete the block ────
    // The block is a personal session scheduled toward a goal. Logging the
    // units the user finished is the whole point: POST it to the goal (server
    // adds it to current_value + this week's bar + auto-achieves), then flip
    // the block complete so the calendar tile reads done.
    async function saveGoalUnits() {
        const units = Number(goalUnits);
        if (!goalId || !Number.isFinite(units) || units <= 0) {
            setError(`Enter how many ${unitLabel || "units"} you finished.`);
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await api.post(`/me/goals/${goalId}/log-progress`, { units });
            if (blockId) {
                await api.patch(`/time-blocks/${blockId}`, { is_completed: true });
            }
            onSaved();
            onClose();
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't log progress");
        }
        finally {
            setSaving(false);
        }
    }
    // "Not yet" on a goal session — don't log units, just stamp reviewed_at so
    // the past-block prompt stops nagging (mirrors the free-form Not-yet path).
    async function markGoalReviewed() {
        setSaving(true);
        setError(null);
        try {
            if (blockId) {
                await api.patch(`/time-blocks/${blockId}`, {
                    reviewed_at: new Date().toISOString(),
                });
            }
            onSaved();
            onClose();
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Save failed");
        }
        finally {
            setSaving(false);
        }
    }
    // ── Step 2: percent → save → branch ──────────────────────────────────
    async function savePercentAndBranch() {
        setSaving(true);
        setError(null);
        try {
            const done = percent >= 100;
            // Flex + Kill List sub-flow: we have a pre-selected kill row from
            // the flex_linked_pick step. Delegate to saveFlexKillPath which patches
            // the block with the linkage + is_completed in a single call.
            const handledByFlex = await saveFlexKillPath(done);
            if (handledByFlex) {
                onSaved();
                if (done) {
                    onClose();
                    return;
                }
                // <100 % on a kill-linked flex block: skip the reschedule/handoff
                // prompt — the user already committed to picking the work up via
                // the kill list. Banner above will confirm the re-add.
                onClose();
                return;
            }
            // Non-flex (or flex+ops/other) flavors:
            //   1. Checklist-item block: flip the BLOCK is_completed and let the
            //      backend cascade tick the checklist item and recompute task
            //      progress. We don't patch /tasks directly — the recompute would
            //      override the manual value with the derived ratio.
            //   2. Plain-task block (taskId set, no checklist linkage): patch the
            //      task's progress_percent (manual override, no checklist to
            //      derive from) AND flip the block when done so the calendar
            //      tile reflects it.
            //   3. Free-form block (no task, no checklist): just flip the block
            //      when done.
            if (checklistItemId && blockId) {
                if (done) {
                    await api.patch(`/time-blocks/${blockId}`, { is_completed: true });
                }
                else {
                    // < 100 %: don't flip is_completed (would cascade and tick the
                    // checklist item even though the user reported partial
                    // progress). Stamp reviewed_at instead so the calendar tile
                    // dims + the "Complete?" prompt disappears — the block is
                    // handled, just not the underlying task.
                    await api.patch(`/time-blocks/${blockId}`, {
                        reviewed_at: new Date().toISOString(),
                    });
                }
            }
            else if (taskId) {
                await api.patch(`/tasks/${taskId}`, { progress_percent: percent });
                if (blockId && done) {
                    await api.patch(`/time-blocks/${blockId}`, { is_completed: true });
                }
                else if (blockId) {
                    // Same rationale as the checklist branch — partial progress
                    // shouldn't flip the block to is_completed; just review it.
                    await api.patch(`/time-blocks/${blockId}`, {
                        reviewed_at: new Date().toISOString(),
                    });
                }
            }
            else if (blockId) {
                if (done) {
                    await api.patch(`/time-blocks/${blockId}`, { is_completed: true });
                }
                else {
                    // Free-form block with no linkage — mark reviewed so the prompt
                    // disappears even when the user picked "Not yet".
                    await api.patch(`/time-blocks/${blockId}`, {
                        reviewed_at: new Date().toISOString(),
                    });
                }
            }
            onSaved();
            if (percent >= 100) {
                // 100 auto-completes the task server-side — done.
                onClose();
                return;
            }
            // <100 → the task isn't finished; re-add it to the caller's Kill List
            // so it bubbles back to the top of their week, and so the kill-list
            // endpoint's auto-claim re-asserts them as a TaskAssignment (which is
            // what keeps the task visible in Mgmt Assigned for mgmt-created
            // projects). Best-effort: a 409 just means they already have a kill
            // row for this task/item this week — which is fine, the goal is met.
            //
            // Prefer checklist_item_id when both are present. That's the more
            // specific ref AND matches what +Kill writes when scheduling from
            // a checklist item, so the backend dedup query (extended to
            // cross-reference task_id <-> checklist_item_id.task_id) hits the
            // existing row reliably and returns 409 instead of accidentally
            // creating a duplicate task-id-only row. The user-facing symptom
            // of the old behaviour was "after Not yet + Reschedule I see two
            // greyed copies of the same task on my Kill List."
            const killBody = {
                week_start: mondayOfThisWeekISO(),
            };
            if (checklistItemId) {
                killBody.checklist_item_id = checklistItemId;
            }
            else if (taskId) {
                killBody.task_id = taskId;
            }
            if (killBody.task_id || killBody.checklist_item_id) {
                try {
                    await api.post("/kill-list", killBody);
                    setKillListAdded(true);
                    setKillListReason(null);
                }
                catch (e) {
                    if (e instanceof ApiError && e.status === 409) {
                        // 409 is the dedup case only — the task is already on this
                        // week's kill list. Same outcome from the user's POV: it's
                        // on the list, so show the success banner.
                        setKillListAdded(true);
                        setKillListReason(null);
                    }
                    else {
                        // 422 (dependency gate) or anything else means the row did
                        // NOT land. Surface the server's actual detail so the user
                        // sees the real reason ("finish task X first") in the
                        // banner instead of a silent miss disguised as success.
                        setKillListAdded(false);
                        setKillListReason(e instanceof Error && e.message
                            ? e.message
                            : null);
                    }
                }
            }
            // <100 → ask whether to reschedule or hand off.
            setStep("reschedule_or_handoff");
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Save failed");
        }
        finally {
            setSaving(false);
        }
    }
    // saveFreeForm() was removed when the modal was refactored so every
    // answer routes through the percent step (savePercentAndBranch handles
    // the free-form case in-line by patching block.is_completed at 100 %).
    // Leaving the function as dead code tripped tsc's noUnusedLocals and
    // failed the Railway build.
    // ── Step 3a: Reschedule ──────────────────────────────────────────────
    function pickReschedule() {
        onScheduleFollowUp(percent);
        onClose();
    }
    // ── Step 3b: Handoff — lazy-load coworkers when we enter this step ───
    async function pickHandoff() {
        setStep("handoff_pick");
        setCoworkersLoading(true);
        try {
            const list = await api.get("/users/coworkers");
            setCoworkers(list);
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't load coworkers");
        }
        finally {
            setCoworkersLoading(false);
        }
    }
    async function submitHandoff() {
        if (!taskId || !handoffUserId)
            return;
        setSaving(true);
        setError(null);
        try {
            await api.post(`/tasks/${taskId}/handoff`, {
                to_user_id: handoffUserId,
                note: handoffNote.trim() || null,
            });
            onSaved();
            onClose();
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't request handoff");
        }
        finally {
            setSaving(false);
        }
    }
    // ── Flex Category step → branches into Linked / OPS / Other ─────────
    async function pickFlexCategory(cat) {
        setFlexCategory(cat);
        setError(null);
        if (cat === "linked") {
            // Lazy-load the picker's contents — the caller's kill list + their
            // own incomplete Deep Focus blocks for the next two weeks. Both
            // sources collapse into one dropdown so the user picks "the thing
            // I was working on" without having to know which list it lives on.
            // Best-effort: any source that fails falls through to an empty
            // contribution.
            setStep("flex_linked_pick");
            setFlexKillLoading(true);
            try {
                const opts = await loadFlexLinkedOptions();
                setFlexLinkedOptions(opts);
            }
            catch {
                setFlexLinkedOptions([]);
            }
            finally {
                setFlexKillLoading(false);
            }
        }
        else if (cat === "ops") {
            // The BU + activity catalogs come from useOrgCatalog (already loaded /
            // loading via the hook), so just show the step.
            setStep("flex_ops");
        }
        else {
            // Other → "Something Non ArtistBeat related". Reclassify the block
            // as Personal so it stops counting toward Flex totals + lands in
            // the Personal Time bucket on the admin Time-per-Activity widget.
            saveFlexOther();
        }
    }
    // Fetch both sources for the linked-task picker. Kill list lookup is
    // unchanged. Deep Focus blocks come from /time-blocks for the current
    // ISO week; we keep only the caller's lockout blocks that aren't yet
    // is_completed AND carry a task linkage (a free-form Deep Focus block
    // without project linkage has nothing to point this Flex block at).
    async function loadFlexLinkedOptions() {
        const opts = [];
        const dedupKeys = new Set();
        // 1) Kill list rows.
        try {
            const kill = await api.get("/kill-list");
            for (const k of kill) {
                opts.push({
                    key: `kill:${k.id}`,
                    source: "kill",
                    label: k.project_name
                        ? `${k.project_name} — ${k.title}`
                        : k.title,
                    task_id: k.task_id ?? null,
                    checklist_item_id: k.checklist_item_id ?? null,
                });
                // Dedup key for the deep-focus pass below — by underlying task
                // / checklist item, so we don't list the same task twice when it
                // happens to be on both lists.
                if (k.task_id)
                    dedupKeys.add(`t:${k.task_id}`);
                if (k.checklist_item_id)
                    dedupKeys.add(`c:${k.checklist_item_id}`);
            }
        }
        catch {
            /* swallow — at worst the picker shows only deep focus options */
        }
        // 2) Incomplete Deep Focus blocks on the caller's calendar this week
        //    + next week — gives the user a reasonable horizon without
        //    flooding the dropdown.
        try {
            const today = new Date();
            const fromStr = (() => {
                const d = new Date(today);
                const dow = (d.getDay() + 6) % 7; // Mon = 0
                d.setDate(d.getDate() - dow);
                return ymdLocal(d);
            })();
            const toStr = (() => {
                const d = new Date(today);
                const dow = (d.getDay() + 6) % 7;
                d.setDate(d.getDate() - dow + 14); // two weeks
                return ymdLocal(d);
            })();
            const blocks = await api.get(`/time-blocks?from=${fromStr}&to=${toStr}`);
            for (const b of blocks) {
                if (b.is_completed)
                    continue;
                if (b.block_type !== "lockout")
                    continue;
                if (!b.task_id && !b.checklist_item_id)
                    continue;
                const dk = b.checklist_item_id
                    ? `c:${b.checklist_item_id}`
                    : b.task_id
                        ? `t:${b.task_id}`
                        : null;
                if (dk && dedupKeys.has(dk))
                    continue;
                if (dk)
                    dedupKeys.add(dk);
                const label = b.project_name && b.task_title
                    ? `${b.project_name} — ${b.task_title}`
                    : b.title;
                opts.push({
                    key: `block:${b.id}`,
                    source: "block",
                    label: `Deep Focus · ${label}`,
                    task_id: b.task_id,
                    checklist_item_id: b.checklist_item_id,
                });
            }
        }
        catch {
            /* swallow — at worst the picker shows only kill list options */
        }
        return opts;
    }
    // Flex "Other — Something Non ArtistBeat related" — reclassify the
    // block as Personal so it stops counting toward Flex totals and lands
    // in the Personal Time bucket on Time-per-Activity. is_personal mirrors
    // block_type for any legacy code paths still keying off the boolean.
    async function saveFlexOther() {
        if (!blockId) {
            onClose();
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await api.patch(`/time-blocks/${blockId}`, {
                block_type: "personal",
                is_personal: true,
                is_completed: true,
            });
            onSaved();
            onClose();
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Save failed");
        }
        finally {
            setSaving(false);
        }
    }
    // Flex "OPS / Admin" — classify the time under a business unit + activity
    // (e.g. OPS + Admin) and flip is_completed. Writing block-level BU/activity
    // is what keeps this flex time OUT of the reports' "Unassigned" column. The
    // block's title stays "Flex"; the optional description carries the activity
    // log ("checked emails", "planned schedule", etc.) for the week summary.
    async function saveFlexOps() {
        if (!blockId) {
            onClose();
            return;
        }
        if (!opsActivityId) {
            setError("Pick an activity so this time is categorized correctly.");
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await api.patch(`/time-blocks/${blockId}`, {
                business_unit_id: opsBuId || null,
                activity_id: opsActivityId,
                description: flexDesc.trim() || null,
                is_completed: true,
            });
            onSaved();
            onClose();
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Save failed");
        }
        finally {
            setSaving(false);
        }
    }
    // Flex "Linked task" — after the user picks which task they worked on
    // (from the merged kill-list + incomplete Deep Focus dropdown), we move
    // into the percent step. The actual save (linking the block + maybe
    // re-adding to kill list) happens in savePercentAndBranch, which we
    // reuse — flexSelectedKey seeds the linkage during that save.
    function flexKillPicked() {
        const row = flexLinkedOptions.find((r) => r.key === flexSelectedKey);
        if (!row) {
            setError("Pick what you were working on before continuing.");
            return;
        }
        // Default percent: 100 (assume done) — user can dial back. Mirrors
        // the existing answerDone(true) seed.
        setPercent(100);
        setStep("percent");
    }
    // For flex_linked_pick → percent → save, we patch the block with the
    // chosen checklist_item_id (or task_id) so the backend cascades the
    // completion and re-uses the kill auto-removal helpers. Runs only
    // when the user is in the flex flow. Returns true if it handled the
    // save (caller should skip the normal logic).
    async function saveFlexKillPath(done) {
        if (flexCategory !== "linked" || !flexSelectedKey || !blockId)
            return false;
        const row = flexLinkedOptions.find((r) => r.key === flexSelectedKey);
        if (!row)
            return false;
        // Patch the block to link it AND mark complete in one call. Backend
        // cascade handles ticking the checklist item + recomputing task
        // progress when checklist_item_id is set; the auto-delete-kill-row
        // logic in create_block doesn't fire on PATCH, so for sub-100% the
        // re-add-to-kill-list logic below ensures the row stays available.
        // For done=true: flip is_completed so the cascade ticks the linked
        // checklist item / task as part of the normal completion path. For
        // done=false: stamp reviewed_at instead so the calendar tile dims +
        // the "Complete?" prompt disappears, but the cascade DOESN'T fire
        // (the user just said the task isn't done). Same reviewed_at logic
        // as the non-flex Not-yet branch above.
        const blockPatch = {
            checklist_item_id: row.checklist_item_id ?? null,
            task_id: row.task_id ?? null,
        };
        if (done) {
            blockPatch.is_completed = true;
        }
        else {
            blockPatch.reviewed_at = new Date().toISOString();
        }
        await api.patch(`/time-blocks/${blockId}`, blockPatch);
        // <100 % and we have a task → make sure the kill row stays on the
        // user's list so they pick it up next week (the cascade only deletes
        // on full completion).
        if (!done && row.task_id) {
            try {
                await api.post("/kill-list", {
                    task_id: row.task_id,
                    week_start: mondayOfThisWeekISO(),
                });
                setKillListAdded(true);
            }
            catch (e) {
                if (e instanceof ApiError && e.status === 409) {
                    setKillListAdded(true);
                }
                else {
                    setKillListAdded(false);
                }
            }
        }
        return true;
    }
    return (_jsx("div", { className: "modal-backdrop", onClick: onClose, children: _jsxs("div", { className: "modal", onClick: (e) => e.stopPropagation(), children: [_jsxs("h2", { children: [step === "complete_q" && "Was this completed?", step === "time_taken" && "How long did it take?", step === "goal_units" &&
                            (unitLabel ? `How many ${unitLabel} did you finish?` : "How much did you finish?"), step === "percent" && "How far did you get?", step === "reschedule_or_handoff" && "Finish later — or hand it off?", step === "handoff_pick" && "Pick who you want to hand this off to", step === "flex_category" && "What did you spend this Flex on?", step === "flex_linked_pick" && "Pick what you were working on", step === "flex_ops" && "Log a quick description"] }), _jsx("p", { className: "muted small", style: { marginTop: "-0.5rem" }, children: blockTitle }), step === "flex_category" && (_jsxs(_Fragment, { children: [_jsx("p", { className: "muted small", children: "Flex time is for everything in between focused work. Tell us what bucket this block fell into so your week summary stays accurate." }), _jsxs("div", { className: "row", style: { marginTop: "0.6rem", flexDirection: "column", gap: "0.5rem" }, children: [_jsx("button", { type: "button", className: "duration-btn", disabled: saving, onClick: () => pickFlexCategory("linked"), style: { width: "100%", textAlign: "left" }, children: "A task on my schedule \u2014 Kill List or an open Deep Focus block" }), _jsx("button", { type: "button", className: "duration-btn", disabled: saving, onClick: () => pickFlexCategory("ops"), style: { width: "100%", textAlign: "left" }, children: "OPS / Admin \u2014 log it under a business unit + activity" }), _jsx("button", { type: "button", className: "duration-btn", disabled: saving, onClick: () => pickFlexCategory("other"), style: { width: "100%", textAlign: "left" }, title: "Reclassifies this block as Personal so it stops counting as Flex", children: "Other \u2014 Something Non ArtistBeat related" })] })] })), step === "flex_linked_pick" && (_jsxs(_Fragment, { children: [_jsx("p", { className: "muted small", children: "Which task did you make progress on? We list every Kill List row + every open Deep Focus block on your schedule for the next two weeks." }), _jsxs("label", { children: ["Task", _jsxs("select", { value: flexSelectedKey, onChange: (e) => setFlexSelectedKey(e.target.value), disabled: flexKillLoading, children: [_jsx("option", { value: "", children: flexKillLoading
                                                ? "Loading…"
                                                : flexLinkedOptions.length === 0
                                                    ? "Nothing scheduled to pick from"
                                                    : "Pick a task you were on…" }), flexLinkedOptions.map((o) => (_jsx("option", { value: o.key, children: o.label }, o.key)))] })] }), _jsxs("div", { className: "modal-actions", children: [_jsx("button", { type: "button", onClick: () => setStep("flex_category"), disabled: saving, children: "Back" }), _jsx("button", { type: "button", className: "btn-primary", onClick: flexKillPicked, disabled: saving || !flexSelectedKey, children: "Continue" })] })] })), step === "flex_ops" && (_jsxs(_Fragment, { children: [_jsx("p", { className: "muted small", children: "Categorize this time so it lands in the right bucket on the team reports (e.g. OPS + Admin). Pick the business unit and activity, and optionally jot what you did." }), _jsxs("div", { className: "row", children: [_jsxs("label", { children: ["Business unit", _jsxs("select", { value: opsBuId, onChange: (e) => setOpsBuId(e.target.value), disabled: orgsLoading, children: [_jsx("option", { value: "", children: orgsLoading ? "Loading…" : "— none —" }), orgBusinessUnits.map((b) => (_jsx("option", { value: b.id, children: b.name }, b.id)))] })] }), _jsxs("label", { children: ["Activity", _jsxs("select", { value: opsActivityId, onChange: (e) => setOpsActivityId(e.target.value), disabled: orgsLoading, children: [_jsx("option", { value: "", children: orgsLoading ? "Loading…" : "Pick an activity…" }), orgActivities.map((a) => (_jsx("option", { value: a.id, children: a.name }, a.id)))] })] })] }), _jsxs("label", { children: ["Description ", _jsx("span", { className: "muted small", children: "\u00B7 optional" }), _jsx("textarea", { rows: 2, value: flexDesc, placeholder: "Cleared inbox, replied to teammates, planned the rest of the week", onChange: (e) => setFlexDesc(e.target.value) })] }), _jsxs("div", { className: "modal-actions", children: [_jsx("button", { type: "button", onClick: () => setStep("flex_category"), disabled: saving, children: "Back" }), _jsx("button", { type: "button", className: "btn-primary", onClick: saveFlexOps, disabled: saving || !opsActivityId, children: saving ? "Saving…" : "Save & mark complete" })] })] })), step === "complete_q" && (_jsxs(_Fragment, { children: [isGoal ? (_jsxs("p", { className: "muted small", children: ["This is a goal session. Tell us whether you did it \u2014 then you'll log how ", unitLabel ? `many ${unitLabel}` : "much", " you finished toward the goal."] })) : (!isLinkedTask && (_jsx("p", { className: "muted small", children: "This block isn't linked to a project task. Just let us know whether the work got done." }))), _jsxs("div", { className: "row", style: { marginTop: "0.75rem", gap: "0.6rem" }, children: [_jsx("button", { type: "button", className: "duration-btn", disabled: saving, onClick: () => answerDone(true), children: "\u2713 Done" }), _jsx("button", { type: "button", className: "duration-btn", disabled: saving, onClick: () => answerDone(false), children: "Not yet" })] })] })), step === "time_taken" && (_jsxs(_Fragment, { children: [_jsxs("p", { className: "muted small", children: ["This block was scheduled for", " ", _jsxs("strong", { children: [scheduledMinutes ?? 0, " min"] }), ". Confirm that, or enter how long it actually took \u2014 we'll round to the nearest 15 minutes and trim the block to match so your time-spent totals stay accurate."] }), _jsxs("label", { children: ["Time spent (minutes)", _jsx("input", { type: "number", min: 0, step: 5, value: actualMinutes, autoFocus: true, onChange: (e) => setActualMinutes(Math.max(0, Number(e.target.value) || 0)) })] }), _jsxs("div", { className: "muted small", style: { marginTop: "0.3rem" }, children: ["Recording", " ", _jsxs("strong", { children: [roundTo15(actualMinutes), " min"] }), scheduledMinutes != null &&
                                    roundTo15(actualMinutes) !== scheduledMinutes
                                    ? ` — block trimmed from ${scheduledMinutes} min.`
                                    : "."] }), _jsxs("div", { className: "modal-actions", children: [_jsx("button", { type: "button", onClick: () => setStep("complete_q"), disabled: saving, children: "Back" }), _jsx("button", { type: "button", className: "btn-primary", onClick: saveCompleteWithTime, disabled: saving, children: saving ? "Saving…" : "Save & complete" })] })] })), step === "goal_units" && (_jsxs(_Fragment, { children: [_jsxs("p", { className: "muted small", children: ["Log how ", unitLabel ? `many ${unitLabel}` : "much", " you actually finished this session \u2014 we'll add it to your goal's progress", goalSessionTarget != null ? (_jsxs(_Fragment, { children: [" ", "(you planned ", _jsxs("strong", { children: [goalSessionTarget, unitLabel ? ` ${unitLabel}` : ""] }), ")."] })) : (".")] }), _jsxs("label", { children: [unitLabel ? unitLabel[0].toUpperCase() + unitLabel.slice(1) : "Amount finished", _jsx("input", { type: "number", min: 0, step: "any", value: goalUnits, autoFocus: true, placeholder: unitLabel ? `e.g. 6.5 ${unitLabel}` : "e.g. 6.5", onChange: (e) => setGoalUnits(e.target.value), onKeyDown: (e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            void saveGoalUnits();
                                        }
                                    } })] }), _jsxs("div", { className: "modal-actions", children: [_jsx("button", { type: "button", onClick: () => setStep("complete_q"), disabled: saving, children: "Back" }), _jsx("button", { type: "button", className: "btn-primary", onClick: saveGoalUnits, disabled: saving || !(Number(goalUnits) > 0), children: saving ? "Saving…" : "Save & complete" })] })] })), step === "percent" && (_jsxs(_Fragment, { children: [_jsxs("p", { className: "muted small", children: ["Update how much of the task is done. ", percent === 100
                                    ? "Saving 100% marks the task complete."
                                    : "We'll save your progress to the task, then ask whether you want to reschedule the rest or hand it off."] }), _jsxs("label", { children: ["Progress", _jsx("input", { type: "range", min: 0, max: 100, step: 5, value: percent, onChange: (e) => setPercent(Number(e.target.value)) })] }), _jsxs("div", { className: "row", style: { alignItems: "center" }, children: [_jsxs("strong", { style: { fontSize: "1.2rem" }, children: [percent, "%"] }), _jsx("input", { type: "number", min: 0, max: 100, value: percent, onChange: (e) => setPercent(Math.max(0, Math.min(100, Number(e.target.value) || 0))), style: { width: "5rem" } })] }), percent === 100 && (_jsxs("div", { className: "flex-banner", style: { marginTop: "0.6rem" }, children: [_jsx("div", { className: "flex-title", children: "Marking task complete" }), _jsxs("div", { className: "flex-desc", children: ["Saving 100% flips the linked task to", " ", _jsx("strong", { children: "completed" }), " in Projects & Meetings."] })] }))] })), step === "reschedule_or_handoff" && (_jsxs(_Fragment, { children: [killListAdded === true && (_jsxs("div", { className: "flex-banner", style: { marginBottom: "0.6rem" }, children: [_jsx("div", { className: "flex-title", children: "Back on your Kill List" }), _jsx("div", { className: "flex-desc", children: "We've added this task back to your Kill List for this week and re-assigned it to you so it shows up under Mgmt Assigned in the scheduler." })] })), killListAdded === false && (_jsxs("div", { className: "flex-banner", style: { marginBottom: "0.6rem" }, children: [_jsx("div", { className: "flex-title", children: "Couldn't add to Kill List" }), _jsx("div", { className: "flex-desc", children: killListReason ? (_jsxs(_Fragment, { children: [killListReason, " ", _jsx("span", { className: "muted small", children: "Your progress saved either way \u2014 once the blocker clears, use \"+ Kill\" on the task in the scheduler." })] })) : (_jsx(_Fragment, { children: "Your progress saved, but we couldn't auto-add this to your Kill List. Open the scheduler and use \"+ Kill\" on the task to put it back on this week's list." })) })] })), _jsxs("p", { className: "muted small", children: ["You're at ", _jsxs("strong", { children: [percent, "%"] }), ". Pick what's next:"] }), _jsxs("div", { className: "row", style: { gap: "0.6rem", marginTop: "0.5rem", flexWrap: "wrap" }, children: [_jsx("button", { type: "button", className: "btn-primary claim-btn", onClick: pickReschedule, title: "Drop a follow-up block on your own calendar now", children: "+ Reschedule a follow-up" }), _jsx("button", { type: "button", className: "duration-btn", onClick: onClose, title: "Leave the task on your Kill List for this week; you'll pick a time later", children: "Schedule later" }), _jsx("button", { type: "button", className: "duration-btn", onClick: pickHandoff, title: "Ask a coworker to take it from here (manager/admin must approve)", children: "Hand off to a coworker" })] }), _jsx("p", { className: "muted small", style: { marginTop: "0.5rem" }, children: "Handoffs need manager or admin approval before they reassign the task." })] })), step === "handoff_pick" && (_jsxs(_Fragment, { children: [_jsxs("label", { children: ["Coworker", _jsxs("select", { value: handoffUserId, onChange: (e) => setHandoffUserId(e.target.value), disabled: coworkersLoading, children: [_jsx("option", { value: "", children: coworkersLoading
                                                ? "Loading…"
                                                : coworkers.length === 0
                                                    ? "No teammates available"
                                                    : "Pick a teammate…" }), coworkers.map((u) => (_jsxs("option", { value: u.id, children: [u.name, " (", u.email, ")"] }, u.id)))] })] }), _jsxs("label", { children: ["Note for the reviewer", " ", _jsx("span", { className: "muted small", children: "\u00B7 optional" }), _jsx("textarea", { rows: 3, placeholder: "Context for the manager/admin \u2014 why you're handing it off, what's done, what's left.", value: handoffNote, onChange: (e) => setHandoffNote(e.target.value) })] }), _jsx("p", { className: "muted small", children: "Your manager or an admin will review and either approve (the task reassigns to the coworker) or reject." })] })), error && _jsx("div", { className: "error", children: error }), _jsxs("div", { className: "modal-actions", children: [_jsx("button", { onClick: onClose, disabled: saving, children: "Cancel" }), step === "percent" && (_jsx("button", { onClick: savePercentAndBranch, className: "btn-primary", disabled: saving, children: saving
                                ? "Saving…"
                                : percent === 100
                                    ? "Save & complete"
                                    : "Save progress" })), step === "handoff_pick" && (_jsx("button", { onClick: submitHandoff, className: "btn-primary", disabled: saving || !handoffUserId, children: saving ? "Submitting…" : "Request handoff" }))] })] }) }));
}
